import { useEffect, useState } from 'react'
import { useRequestState } from '../useRequestState'
import { TrendPopup } from './TrendPopup'
import { AREA_LEAVES, AREA_SEGMENTS, AREA_UNIT, SEGMENT_AREAS, segmentApiValue, segmentLabel, type Area, type Segment } from '../segments'
import { fetchSkuList, type SkuListResponse, type SkuListGran } from '../landing'
import { TENANT } from '../tenant'
import { formatDayShort, formatMonth, formatPercent, formatQuantity, formatShare, listChange, MISSING, plural, type ListChange } from '../format'
import { InfoTip } from './InfoTip'
import { SearchableSelect } from './SearchableSelect'
import { ShareBasisLine } from './ShareBasisLine'
import { articleTitle, brandLabel, isBrandMissing, isSkuMissing, KIT_INFO, kitContentsText, kitInfo, STRENGTH_INFO, strengthText } from '../missingLabels'

const GRAN_LABEL: Record<SkuListGran, string> = { week: 'Week', month: 'Month', year: 'Last 12 months' }
// Rank-kolumnens tooltip måste följa vald granularitet — inte alltid "månad" (MP-OS-79/80).
const RANK_PERIOD: Record<SkuListGran, string> = { week: 'this week', month: 'this month', year: 'this year' }

// MP-OS-49 — the full per-SKU dosor list (à la the VM Excel, but trendable): every
// article for the logged-in supplier, latest month with the change vs the previous
// month. A clean, scrollable table — lives in Försäljning, not on the overview.
const monthLabel = (m: string) => formatMonth(m, { short: true })

// MP-OS-191 follow-up: "1 article", "2 articles" — never "1 articles" — and a missing count
// (sku_count absent, e.g. an ungrouped row) shows the dash alone, with no dangling noun.
const skuCountLabel = (count: number | null | undefined): string =>
  count == null ? MISSING : `${count} ${plural(count, 'article')}`

// MPD-132: change and rank follow one rule (format.ts listChange): "new" only when the row has
// no previous rank AND no previous volume; a row with an earlier rank but no computable change
// shows "—", never "new".
function Change({ change, available = true }: { change: ListChange; available?: boolean }) {
  if (change.kind === 'none') return <span className="sku-delta sku-delta-na"
    title={available ? 'No change to show: the previous period has no positive CU for this row' : 'No comparable period available'}>{MISSING}</span>
  if (change.kind === 'new') return <span className="sku-delta sku-delta-na">new</span>
  // MP-OS-206: a change that rounds to 0.0 % is neutral: grey, no arrow.
  if (change.direction === 'flat') return <span className="sku-delta sku-delta-flat tnum">{formatPercent(0)}</span>
  const up = change.direction === 'up'
  return (
    <span className={`sku-delta ${up ? 'sku-delta-up' : 'sku-delta-down'} tnum`}>
      {up ? '▲' : '▼'} {formatPercent(Math.abs(change.pct))}
    </span>
  )
}

// Placement this month + (previous month) in parentheses — MP-OS-79/80. A rising rank
// (smaller number) is green, a slipping one red; "(new)" only for a truly new row (listChange).
function Rank({ rank, prev, change, available = true }: { rank: number; prev: number | null; change: ListChange; available?: boolean }) {
  const move = prev == null ? 'na' : rank < prev ? 'up' : rank > prev ? 'down' : 'flat'
  const cls = move === 'up' ? 'sku-delta-up' : move === 'down' ? 'sku-delta-down' : ''
  return (
    <span className="tnum">
      {rank}
      <span className={`sku-rank-prev ${cls}`} style={{ marginLeft: 4, fontSize: 11 }}>
        {!available || prev == null ? change.kind === 'new' ? '(new)' : '(—)' : `(${prev})`}
      </span>
    </span>
  )
}

export function SkuList({
  month,
  by = 'sku',
  area,
  gran: granProp,
  week,
  segment: segmentProp,
  onSegmentChange,
  leaf: leafProp,
  onLeafChange,
  historyFrom,
  dataThrough,
}: {
  month?: string
  by?: 'sku' | 'brand'
  // When set, this list is one product-area block (Olof's per-area top lists): the data is
  // scoped to the area, the unit follows the area (CU vs units), and the segment picker is
  // hidden — the block IS the area, so a global segment filter would be redundant.
  area?: Area
  // Controlled granularity + specific week (from the shared top-lists time control). When
  // `gran` is provided the internal week/month/year toggle is hidden — one control drives
  // every area block, instead of each block carrying its own (Olof: the double time-control
  // was confusing). `week` (a date in the target ISO week) picks a specific week.
  gran?: SkuListGran
  week?: string
  // Controlled category filter (MP-OS-135, Olof 18/9: "valet av kategori under en av
  // stackarna"). A block holds TWO lists — articles and brands — and each used to carry its own
  // segment/sub-category picker, so a choice under one block only steered half of it. When the
  // parent passes these, the block owns ONE picker and both lists follow it; the internal
  // pickers are hidden, the same way `gran` already works.
  segment?: Segment
  onSegmentChange?: (s: Segment) => void
  leaf?: string
  onLeafChange?: (l: string) => void
  historyFrom?: string
  dataThrough?: string
}) {
  // Every area is counted in CU (Block 9); default CU.
  const unit = area ? AREA_UNIT[area] : 'CU'
  // Vecka / Månad / År window (MP-OS-81). Controlled by the parent when `gran` is passed.
  const [granState, setGran] = useState<SkuListGran>('month')
  const gran = granProp ?? granState
  // Brand filter (MP-OS-83) — "Alla" plus each own brand, so BAT can look at Velo vs
  // Lundgren separately, just like the overview's per-märke toggle.
  const [brandFilter, setBrandFilter] = useState('All')
  // "Visa även konkurrenter" (MP-OS-84) — the whole-market leaderboard across all brands.
  // Olof-approved: dosor + placering only, no prices, so it's harmless.
  const [market, setMarket] = useState(false)
  // MP-OS-103 — ONE list plus a segment selector, not the three separate lists their report
  // ships. Olof: "Alla de här kan ju du bara låta mig välja. Så kommer det olika topplistor."
  const [segmentState, setSegmentState] = useState<Segment>('ALL')
  const segment = segmentProp ?? segmentState
  const setSegment = onSegmentChange ?? setSegmentState
  // Vape/Smoking sub-category (leaf) filter — the pod/disposable, cigg/heat-not-burn equivalent
  // of the snus segment filter (#3). Empty = the whole area.
  const [leafState, setLeafState] = useState('')
  const leaf = leafProp ?? leafState
  const setLeaf = onLeafChange ?? setLeafState
  // Keep every hook above conditional returns. A supplier with no rows in one area
  // transitions from loading to an empty card; changing hook order crashed Top lists.
  const [attempt, setAttempt] = useState(0)
  const requestKey = JSON.stringify([TENANT.manufacturer, market, gran, month, segment, by, area, week, leaf, attempt])
  const [data, setData] = useRequestState<SkuListResponse | null>(requestKey, null)
  const [state, setState] = useRequestState<'loading' | 'ready' | 'empty' | 'error'>(requestKey, 'loading')
  const [openRow, setOpenRow] = useRequestState<{ sku?: string; brandName?: string; title: string } | null>(requestKey, null)

  useEffect(() => {
    let cancelled = false
    setState('loading')
    setOpenRow(null)
    fetchSkuList(market, gran, month, segmentApiValue(segment), by, area, week, leaf || undefined)
      .then((d) => {
        if (cancelled) return
        setData(d)
        setBrandFilter('All')
        setState(d && d.rows.length ? 'ready' : 'empty')
      })
      .catch(() => !cancelled && setState('error'))
    return () => {
      cancelled = true
    }
  }, [market, gran, month, segment, by, area, week, leaf, attempt])

  // A user filter is active when the segment/leaf is narrowed or competitors are on.
  const filtered = segment !== 'ALL' || !!leaf || market
  // Hide a per-area block only when the area is genuinely absent (unfiltered AND empty). Once a
  // filter narrows it to an empty sub-segment, keep the card and its controls visible — else the
  // whole block vanishes and the user can't switch back (Olof 31/8: "trillade ur topplistan").
  if (area && state === 'empty' && !filtered) return null

  // The brand filter offers identified brands only: a missing brand is not a brand to compare,
  // and its articles stay in the list under All (MP-OS-197).
  const brands = data && by === 'sku'
    ? ['All', ...new Set(data.rows.flatMap((r) => isBrandMissing(r.brand) ? [] : [r.brand!.trim()]))] : ['All']
  // MP-OS-144 — a row is a moment in time; clicking it opens that row's curve so a big
  // change reads as a trend or a one-off without leaving the list.
  // All visible competition rows use the restricted CU/share endpoint. Private article
  // endpoints retain their existing own-manufacturer guards.
  const rowTitle = (r: SkuListResponse['rows'][number]) => by === 'brand' ? brandLabel(r.brand) : articleTitle(r.sku, r.namn_sv, r.kit, r.pack_variant, r.kit_contents)
  // MP-OS-197: the remainder row (no identified brand) and an article without a SKU have no curve
  // of their own to open, so they get no drill.
  const drillable = (r: SkuListResponse['rows'][number]) => by === 'brand'
    ? !r.unbranded && !isBrandMissing(r.brand) : !isSkuMissing(r.sku)
  const openTrend = (r: SkuListResponse['rows'][number]) => drillable(r) && setOpenRow(
    by === 'brand' ? { brandName: r.brand!.trim(), title: rowTitle(r) } : { sku: r.sku, title: rowTitle(r) },
  )
  const visibleRows = data
    ? brandFilter === 'All'
      ? data.rows
      : data.rows.filter((r) => r.brand?.trim() === brandFilter)
    : []
  // MPD-135: a kit with reported contents gets an ⓘ with them, and a vape article's strength
  // (as labelled, never mg/ml) is a tag after its name, explained by one ⓘ in the column head.
  const strength = (r: SkuListResponse['rows'][number]) => by === 'sku' ? strengthText(r.strength_label, r.segment) : null
  const anyStrength = visibleRows.some((r) => strength(r))

  return (
    <div className="sku-list-card" aria-busy={state === 'loading'}>
      <div className="sku-list-head">
        <span className="catalog-title">
          {by === 'brand'
            ? market
              ? 'Brands · whole market'
              : `Brands · ${unit} sold`
            : market
              ? 'Top list · whole market'
              : `All articles · ${unit} sold`}
          {/* MPD-64 (L5): one ⓘ for the row badges, instead of one per row. */}
          <InfoTip label="About this list" size="small">
            Your brand / Your article: belongs to the selected supplier according to the catalogue.
            Includes your articles: the brand has articles from your supplier and from other or unmapped
            owners. × 25 doses per bag, × 15 doses per mega can: recounted to standard CU. {MISSING}: no
            comparable period.{data?.rows.some((r) => r.kit) && ` ${KIT_INFO}`}
          </InfoTip>
        </span>
        <span style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
          {state === 'ready' && data && (
            <span className="catalog-tag">
              {data.ongoing
                ? `${monthLabel(data.month)} · ongoing${data.through ? ` · through ${formatDayShort(data.through)}` : ''} · no comparable period`
                : data.comparison_available === false
                ? `${data.granularity === 'month' ? monthLabel(data.month) : data.label} · no comparable period`
                : data.granularity === 'month'
                  ? `${monthLabel(data.month)} · vs ${monthLabel(data.prev)}`
                  : `${data.label} · vs ${data.prev_label}`}
            </span>
          )}
          {segmentProp === undefined && (!area || AREA_SEGMENTS[area].length > 1) && (
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value as Segment)}
              aria-label="Filter by segment"
              className="control-select"
            >
              <option value="ALL">All segments</option>
              {area
                ? // area block: only this area's own sub-segments
                  AREA_SEGMENTS[area].map((sg) => (
                    <option key={sg} value={sg}>
                      {segmentLabel(sg)}
                    </option>
                  ))
                : SEGMENT_AREAS.map((g) => (
                    <optgroup key={g.area} label={g.area}>
                      {g.segments.map((sg) => (
                        <option key={sg} value={sg}>
                          {segmentLabel(sg)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
            </select>
          )}
          {leafProp === undefined && area && AREA_LEAVES[area].length > 0 && (
            <select
              value={leaf}
              onChange={(e) => setLeaf(e.target.value)}
              aria-label="Filter by sub-category"
              className="control-select"
            >
              <option value="">All sub-categories</option>
              {AREA_LEAVES[area].map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          )}
          {granProp === undefined && (
            <span style={{ display: 'inline-flex', gap: 2 }}>
              {(['week', 'month', 'year'] as SkuListGran[]).map((g) => (
                <button
                  key={g}
                  aria-pressed={g === gran}
                  onClick={() => setGran(g)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 8,
                    border: '1px solid var(--border, #ddd)',
                    background: g === gran ? 'var(--brand-dark, #0a7)' : 'transparent',
                    color: g === gran ? '#fff' : 'inherit',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {GRAN_LABEL[g]}
                </button>
              ))}
            </span>
          )}
          {/* U3: "Show competitors too" only means something for a specific supplier \u2014 on the
              "All manufacturers" profile you ARE the whole market already, so the toggle did
              nothing there (Olof: "den funkar ju inte d\u00e4r"). Hidden in that scope. */}
          {TENANT.manufacturer && (
            <button
              aria-pressed={market}
              onClick={() => setMarket((v) => !v)}
              title="Show the whole market\u2019s top list (all brands, no prices)"
              style={{
                padding: '5px 12px',
                borderRadius: 8,
                border: '1px solid var(--border, #ddd)',
                background: market ? 'var(--brand-dark, #0a7)' : 'transparent',
                color: market ? '#fff' : 'inherit',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {market ? 'Showing competitors' : 'Show competitors too'}
            </button>
          )}
        </span>
      </div>
      {state === 'ready' && data ? (
        <>
          {brands.length > 2 && (
            <div style={{ marginBottom: 8 }}>
              <SearchableSelect
                label="Filter by brand"
                searchLabel="Search brands"
                showAllLabel="Show all brands"
                value={brandFilter}
                onChange={setBrandFilter}
                options={brands.map((b) => ({ value: b, label: b }))}
              />
            </div>
          )}
          {data.share_basis && (
            // MP-OS-187: a share always says what it is measured against (MPD-64: the basis stays
            // visible, the full sentence is in the ⓘ).
            <ShareBasisLine basis={data.share_basis} prefix="Share: " label="About the shares"
              className="sku-share-basis" style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--muted, #4a4a4a)' }}
              note={brandFilter !== 'All' ? ' Shares are of the whole list, before the brand filter.' : null} />
          )}
          <div className="sku-table-wrap">
            <table className="sku-table">
              <thead>
                <tr>
                  <th className="sku-num">#<InfoTip label="About the rank" size="small">{`Rank ${RANK_PERIOD[data.granularity]} (previous in parentheses).`}</InfoTip></th>
                  <th>{by === 'brand' ? 'Brand' : 'Article'}{anyStrength && <InfoTip label="About the strength" size="small">{STRENGTH_INFO}</InfoTip>}</th>
                  <th className="sku-col-secondary">{by === 'brand' ? 'Articles' : 'Brand'}</th>
                  <th className="sku-num">{unit}</th>
                  <th className="sku-num">Share{data.share_basis && <InfoTip label="About the share column" size="small">{`${data.share_basis.text}.`}</InfoTip>}</th>
                  <th className="sku-num">Change</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => {
                  const change = listChange({ available: data.comparison_available, pct: r.change_pct, prevRank: r.prev_rank, prevVolume: r.prev_dosor })
                  return (
                  <tr
                    key={by === 'brand' ? (r.unbranded ? 'unbranded:' : `brand:${r.brand}`) : r.sku ? `sku:${r.sku}` : `row:${i}`}
                    className={r.ownership === 'own' || r.ownership === 'mixed' ? `sku-owned${market ? ' sku-owned-market' : ''}` : undefined}
                    onClick={drillable(r) ? () => openTrend(r) : undefined}
                    style={drillable(r) ? { cursor: 'pointer' } : undefined}
                    title={drillable(r) ? 'Show this row over time' : undefined}
                  >
                    <td className="sku-num">
                      <Rank rank={r.rank} prev={r.prev_rank} change={change} available={data.comparison_available} />
                    </td>
                    <td className="sku-name">
                      {drillable(r) ? <button
                          onClick={(e) => { e.stopPropagation(); openTrend(r) }}
                          aria-label={`Show ${rowTitle(r)} over time`}
                          style={{ font: 'inherit', color: 'inherit', background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >{rowTitle(r)}</button> : <span>{rowTitle(r)}</span>}
                      {strength(r) && <span className="sku-strength-tag">{strength(r)}</span>}
                      {by === 'sku' && r.kit && kitContentsText(r.kit_contents) && <InfoTip label="About this kit" size="small">
                        {kitInfo(r.kit_contents)}</InfoTip>}
                      {r.unbranded && <InfoTip label="About articles without an identified brand" size="small">
                        The articles with no identified brand, together. It is not a brand, but its CU is in the total
                        and it is ranked by its CU like any row.</InfoTip>}
                      {(r.ownership === 'own' || r.ownership === 'mixed') && <span className="sku-owner-badge"
                        title={r.ownership === 'mixed' ? 'This brand includes articles from your supplier and other or unmapped owners.' : 'Belongs to the selected supplier according to the catalogue.'}>
                        {r.ownership === 'mixed' ? 'Includes your articles' : by === 'brand' ? 'Your brand' : 'Your article'}
                      </span>}
                      {r.conversion && (
                        <span
                          title={`Recounted to standard CU: ${r.conversion}`}
                          style={{ marginLeft: 6, fontSize: 11, color: 'var(--accent)', whiteSpace: 'nowrap' }}
                        >
                          {r.conversion}
                        </span>
                      )}
                      {/* MP-OS-191 E: on a phone the Brand/Articles column folds in here, so CU,
                          share and change stay on screen without a horizontal scroll. */}
                      <span className="sku-brand-inline">
                        {by === 'brand' ? skuCountLabel(r.sku_count) : brandLabel(r.brand)}
                      </span>
                    </td>
                    <td className="sku-brand sku-col-secondary">{by === 'brand' ? (r.sku_count ?? MISSING) : brandLabel(r.brand)}</td>
                    <td className="sku-num tnum">{formatQuantity(r.dosor)}</td>
                    <td className="sku-num tnum">{formatShare(r.share_pct, { hasSales: r.dosor > 0 })}</td>
                    <td className="sku-num">
                      <Change change={change} available={data.comparison_available} />
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="catalog-empty" role={state === 'error' ? 'alert' : 'status'}>
          {state === 'loading'
            ? 'Loading…'
            : state === 'error'
              ? 'Could not load the articles — try again in a moment.'
              : filtered
                ? 'No articles in this selection — try another segment or turn on competitors.'
                : 'No dose data for this period.'}
          {state === 'error' && <div><button onClick={() => setAttempt((n) => n + 1)}>Try again</button></div>}
        </div>
      )}

      {openRow && data && (
        <TrendPopup
          month={data.month}
          sku={openRow.sku}
          brandName={openRow.brandName}
          title={openRow.title}
          area={area}
          leaf={leaf || undefined}
          segment={segmentApiValue(segment)}
          dataThrough={data.date_to ?? dataThrough}
          historyFrom={historyFrom}
          onClose={() => setOpenRow(null)}
        />
      )}
    </div>
  )
}
